import React, { Dispatch, SetStateAction } from "react";
import { View, StyleSheet } from "react-native";

import CustomText from "../../common/general/CustomText/CustomText";
import CustomSearchInput from "../../common/general/CustomSearchInput/CustomSearchInput";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import FilterDropdown from "../../common/TableControls/FilterDropdown";
import { GradebookActivityType, StudentActivityStatus } from "@axio/graphql/generated/graphql";
import { GradebookMatrix, type GradebookMatrixData } from "./GradebookMatrix";
import type { GradebookStudent } from "./GradebookCell";
import type { DraftWeightingConfig } from "./gradebookWeighting.types";

export interface FilterItem {
  label: string;
  value: string;
}

export interface FacultyGradebookProps {
  courseItems: FilterItem[];
  moduleItems: FilterItem[];
  selectedCourse: string;
  selectedModule: string;
  setSelectedCourse: Dispatch<SetStateAction<string>>;
  setSelectedModule: Dispatch<SetStateAction<string>>;
  courseDropdownOpen: boolean;
  moduleDropdownOpen: boolean;
  setCourseDropdownOpen: Dispatch<SetStateAction<boolean>>;
  setModuleDropdownOpen: Dispatch<SetStateAction<boolean>>;
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  gradebook: GradebookMatrixData | null;
  loading: boolean;
  errorMessage?: string;
  hasCourse: boolean;
  hasRows: boolean;
  weightingConfig?: DraftWeightingConfig | null;
  weightingDisabled?: boolean;
  exportDisabled?: boolean;
  exportLoading?: boolean;
  exportErrorMessage?: string | null;
  onOpenWeighting?: () => void;
  onExportGrades?: () => void;
  onOpenPanel?: (studentId: string, activityId: string, activityType: GradebookActivityType) => void;
  onStudentPress?: (student: GradebookStudent) => void;
  onMaxPointsChange?: (activityId: string, activityType: GradebookActivityType, maxPoints: number) => void;
  onCellStatusAction?: (
    studentId: string,
    activityId: string,
    submissionId: string | null | undefined,
    activityType: GradebookActivityType,
    status: StudentActivityStatus
  ) => void;
}

function FacultyGradebook({
  courseItems,
  moduleItems,
  selectedCourse,
  selectedModule,
  setSelectedCourse,
  setSelectedModule,
  courseDropdownOpen,
  moduleDropdownOpen,
  setCourseDropdownOpen,
  setModuleDropdownOpen,
  searchQuery,
  setSearchQuery,
  gradebook,
  loading,
  errorMessage,
  hasCourse,
  hasRows,
  weightingConfig,
  weightingDisabled = false,
  exportDisabled = false,
  exportLoading = false,
  exportErrorMessage,
  onOpenWeighting,
  onExportGrades,
  onOpenPanel,
  onStudentPress,
  onMaxPointsChange,
  onCellStatusAction,
}: FacultyGradebookProps) {
  return (
    <View>
      <View style={styles.gradebookFilters}>
        <View style={styles.gradebookFiltersLeft}>
          <View style={styles.gradebookFilterGroup}>
            <FilterDropdown
              filterValue={selectedCourse}
              setFilterValue={setSelectedCourse}
              filterDropdownOpen={courseDropdownOpen}
              setFilterDropdownOpen={setCourseDropdownOpen}
              filterItems={courseItems}
              placeholder="All Courses"
            />
          </View>
          <View style={styles.gradebookFilterGroup}>
            <FilterDropdown
              filterValue={selectedModule}
              setFilterValue={setSelectedModule}
              filterDropdownOpen={moduleDropdownOpen}
              setFilterDropdownOpen={setModuleDropdownOpen}
              filterItems={moduleItems}
              placeholder="All Modules"
            />
          </View>
        </View>
        <View style={styles.gradebookSearchContainer}>
          <CustomSearchInput
            placeholder="Search student name"
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        </View>
      </View>

      <View style={styles.legendRow}>
        <View style={styles.legendItems}>
          <View style={styles.legendItem}>
            <View style={[styles.swatch, styles.swatchMissing]} />
            <CustomText style={styles.legendLabel} text="Missing" size="xs" weight="normal" />
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.swatch, styles.swatchNeedsGrading]} />
            <CustomText style={styles.legendLabel} text="Needs Grading" size="xs" weight="normal" />
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.swatch, styles.swatchNeedsReview]} />
            <CustomText style={styles.legendLabel} text="Needs Review" size="xs" weight="normal" />
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.swatch, styles.swatchLate]} />
            <CustomText style={styles.legendLabel} text="Late" size="xs" weight="normal" />
          </View>
          <CustomText style={styles.hintText} text="Tap cell to edit · Hold for details" size="xs" weight="normal" />
        </View>
        <View style={styles.actionButtons}>
          <CustomButton
            text="Weighting"
            size="s"
            styleType="lightStroke"
            onPress={onOpenWeighting}
            disabled={weightingDisabled}
            leftIcon={null}
            rightIcon={null}
          />
          <CustomButton
            text={exportLoading ? "Exporting..." : "Export Grades"}
            size="s"
            styleType="primaryLight"
            onPress={onExportGrades}
            disabled={exportDisabled || exportLoading}
            leftIcon={null}
            rightIcon={null}
            style={styles.exportButton}
            textStyle={styles.exportButtonText}
          />
        </View>
      </View>
      {exportErrorMessage ? (
        <CustomText text={exportErrorMessage} size="xs" useTranslationText={false} style={styles.exportErrorText} />
      ) : null}

      {!hasCourse ? (
        <CustomText text="Select a course to view the gradebook." size="s" style={styles.gradebookHint} />
      ) : loading && !gradebook ? (
        <CustomText text="Loading gradebook..." size="s" style={styles.gradebookHint} />
      ) : errorMessage ? (
        <CustomText
          text="failed_to_load_gradebook"
          interpolation={{ error: errorMessage }}
          size="s"
          style={styles.gradebookHint}
        />
      ) : !hasRows ? (
        <View style={styles.noRecordsContainer}>
          <CustomText text="No Records Found" size="m" weight="semibold" style={styles.noRecordsText} />
        </View>
      ) : (
        <GradebookMatrix
          data={gradebook}
          weightingConfig={weightingConfig}
          onStudentPress={onStudentPress}
          onOpenGradingSheet={onOpenPanel}
          onMaxPointsChange={onMaxPointsChange}
          onCellStatusAction={onCellStatusAction}
        />
      )}
    </View>
  );
}

export default FacultyGradebook;

const styles = StyleSheet.create({
  gradebookFilters: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
    padding: 16,
    zIndex: 10,
  },
  gradebookFiltersLeft: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
  },
  gradebookSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  gradebookFilterGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  legendRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
    marginTop: 16,
    flexWrap: "wrap",
  },
  legendItems: {
    flexDirection: "row",
    alignItems: "center",
    gap: 20,
    flexWrap: "wrap",
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  swatch: {
    width: 20,
    height: 14,
    borderRadius: 6,
    borderWidth: 1,
  },
  swatchMissing: {
    backgroundColor: "#fef2f2",
    borderColor: "#fecaca",
  },
  swatchNeedsGrading: {
    backgroundColor: "#fffaeb",
    borderColor: "#fde68a",
  },
  swatchNeedsReview: {
    backgroundColor: "#f5f0fe",
    borderColor: "#e8ddfb",
  },
  swatchLate: {
    backgroundColor: "#fef0c7",
    borderColor: "#fde68a",
    borderLeftWidth: 2,
  },
  legendLabel: {
    fontSize: 12,
    color: "#344054",
  },
  hintText: {
    fontSize: 12,
    color: "#475467",
  },
  actionButtons: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  exportButton: {
    borderWidth: 1,
    borderColor: "#04060F1A",
    borderRadius: 8,
  },
  exportButtonText: {
    color: "#3E68FE",
    fontWeight: "700",
    fontSize: 14,
  },
  exportErrorText: {
    color: "#B42318",
    marginBottom: 8,
  },
  gradebookHint: {
    padding: 16,
    color: "#475467",
  },
  noRecordsContainer: {
    padding: 48,
    alignItems: "center",
    justifyContent: "center",
  },
  noRecordsText: {
    color: "#6B7280",
  },
});
